import { useCallback, useContext, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { isFunction } from 'lodash'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'

import { Action, DeleteAction } from '../../../antd/Tables/DataTable/utils/actions'
import CompactedTagList from '../../../shared/Lists/CompactedTagList'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { getName, getSections, getSectors, SectionGroup } from '../../../../utils/entities/sectionGroup'
import { ValidationFields } from '../../../../utils/validators'
import { TableActions } from './SectionGroupsView'
import { SectionGroupModalContext } from '../../../../Providers/Forms/SectionGroupForm/SectionGroupModal'
import DataTable from '../../../antd/Tables/DataTable/DataTable'
import { isNonEmptyArray } from '../../../../utils'
import ColoredTag, { TagColors } from '../../../shared/Lists/ColoredTag'
import { FontSizes } from '../../../icons/IconLabel'

const { ABBREVIATION, NAME } = ValidationFields

const SECTORS_TO_SHOW = 2
const SECTIONS_TO_SHOW = 4

const SectionGroupsTable = ({ data, metadata, loading, tableParameters, onAction, onTableParametersChange }) => {
  const { setFormHandler } = useContext(SectionGroupModalContext)

  const t = useSelector(selectTranslate)

  const openModal = useCallback(sectionGroup => setFormHandler({
    data: new SectionGroup(sectionGroup),
    callback: onAction
  }), [onAction])

  const Sectors = useCallback(group => isNonEmptyArray(getSectors(group)) ? (
    <CompactedTagList
      items={group}
      getter={getSectors}
      label={NAME}
      numberToShow={SECTORS_TO_SHOW}
    />
  ) : (
    <ColoredTag
      tagColor={TagColors.GEEKBLUE}
      fontSize={FontSizes.REGULAR}
      children={<span>*</span>}
    />
  ), [])

  const columns = useMemo(() => [
    { title: t('common.name'), key: getName },
    {
      title: t('common.sections'),
      key: group => (
        <CompactedTagList
          items={group}
          getter={getSections}
          label={ABBREVIATION}
          numberToShow={SECTIONS_TO_SHOW}
        />
      )
    },
    {
      title: t('common.sectors'), key: Sectors
    }
  ], [t])

  const handleAction = useCallback(action => sectionGroup => {
    if (isFunction(onAction)) {
      onAction(sectionGroup, action)
    }
  }, [onAction])

  const actions = useMemo(() => [
    new Action({
      disabled: !isFunction(handleAction()),
      icon: faEdit,
      title: 'common.edit',
      onClick: openModal
    }),
    new DeleteAction({
      disabled: !isFunction(handleAction(TableActions.DELETE)),
      popconfirmTitle: 'section_groups_table.actions.delete.confirm',
      onDelete: handleAction(TableActions.DELETE)
    })
  ], [handleAction, openModal])

  return (
    <DataTable
      columns={columns}
      parameters={tableParameters}
      actions={actions}
      data={data}
      loading={loading}
      metadata={metadata}
      addButtonLabel='section_groups_table.actions.add'
      onParametersChange={onTableParametersChange}
      onCreate={openModal}
    />
  )
}

export default SectionGroupsTable
